import { logger } from '../lib/logger.js';

/**
 * El pitido del fin del descanso.
 *
 * POR QUE NO HAY NI UN FICHERO DE AUDIO NI UNA LIBRERIA
 *
 * Son dos tonos de un cuarto de segundo. Sintetizarlos con la Web Audio API
 * —que existe igual en la PWA de Windows y en el WebView de Android— cuesta
 * treinta lineas y no añade ni un byte al paquete, ni un recurso que copiar a
 * `android/`, ni una descarga que pueda fallar sin red. Un `.mp3` habria
 * traido las cuatro cosas.
 *
 * EL CONTEXTO SE ABRE EN EL GESTO, NO AL SONAR
 *
 * Chrome —y el WebView de Android es Chrome— crea todo `AudioContext` en
 * estado `suspended` si no hay un gesto del usuario detras, y de ahi no sale
 * solo: el pitido no sonaria y no habria ningun error. Por eso
 * `prepararSonido()` se llama al REGISTRAR LA SERIE, que es el toque que
 * arranca el descanso, y `pitidoDeAviso()` un minuto despues ya encuentra el
 * contexto despierto.
 *
 * Con la pantalla apagada esto no suena, y no es un fallo: ahi el temporizador
 * de JavaScript esta congelado y quien avisa es la notificacion programada del
 * sistema, con el sonido y la vibracion de su canal.
 */

type ConstructorDeAudio = new () => AudioContext;

let contexto: AudioContext | null = null;

function constructorDeAudio(): ConstructorDeAudio | undefined {
  return (globalThis as { AudioContext?: ConstructorDeAudio }).AudioContext;
}

/** ¿Se puede sonar en esta plataforma? */
export function haySonido(): boolean {
  return constructorDeAudio() !== undefined;
}

/**
 * Abre —o despierta— el contexto de audio. Se llama DENTRO del gesto.
 *
 * No lanza y no devuelve nada: quedarse sin sonido no puede impedir registrar
 * una serie. Si falla, lo dice el registro y el aviso se queda en vibracion.
 */
export function prepararSonido(): void {
  try {
    const Ctor = constructorDeAudio();
    if (Ctor === undefined) return;
    contexto ??= new Ctor();
    if (contexto.state === 'suspended') {
      void contexto.resume().catch(() => undefined);
    }
  } catch (error) {
    logger.debug('ui', 'AUDIO_UNAVAILABLE', 'No se pudo preparar el sonido', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Un tono, con entrada y salida suaves: un corte seco suena a chasquido. */
function tono(ctx: AudioContext, hz: number, desde: number, duracion: number): void {
  const oscilador = ctx.createOscillator();
  const volumen = ctx.createGain();
  oscilador.type = 'sine';
  oscilador.frequency.value = hz;
  volumen.gain.setValueAtTime(0.0001, desde);
  volumen.gain.exponentialRampToValueAtTime(0.35, desde + 0.015);
  volumen.gain.exponentialRampToValueAtTime(0.0001, desde + duracion);
  oscilador.connect(volumen);
  volumen.connect(ctx.destination);
  oscilador.start(desde);
  oscilador.stop(desde + duracion + 0.02);
}

/**
 * Dos notas cortas, la segunda mas aguda. Medio segundo en total.
 *
 * Breve a proposito: el aviso util es «vuelve a la barra», no una alarma de
 * despertador en mitad de un gimnasio.
 */
export function pitidoDeAviso(): void {
  try {
    prepararSonido();
    const ctx = contexto;
    if (ctx === null || ctx.state !== 'running') {
      logger.debug('ui', 'AUDIO_SUSPENDED', 'Sin sonido: el contexto de audio no esta activo', {
        estado: ctx?.state ?? 'ausente',
      });
      return;
    }
    const ahora = ctx.currentTime;
    tono(ctx, 880, ahora, 0.16);
    tono(ctx, 1318.5, ahora + 0.22, 0.22);
  } catch (error) {
    logger.debug('ui', 'AUDIO_FAILED', 'No se pudo emitir el pitido', {
      message: error instanceof Error ? error.message : String(error),
    });
  }
}

/** Solo para los tests: olvida el contexto abierto. */
export function _resetSonido(): void {
  contexto = null;
}
